/**
 * Step-3 plain audience fields — the monolith's MVP audience panel (index.html
 * L913–930) plus the always-hidden legacy fields the full mode keeps in sync.
 *
 *   MVP (html.mvpaud): this panel IS Step 3 — ".mvp-aud-hd" header ("Audience"
 *   + intro line), Sender Type / UTM Targeting / Email Audience Type combos,
 *   the Salesforce include/exclude textareas (include is REQUIRED and
 *   auto-suggested by autoSF), Sender Profile, and the pilot checkbox.
 *
 *   Full mode: FormView mounts this hidden; the sync below keeps the intake
 *   columns filled exactly like the monolith's hidden fields + syncLegacy().
 *
 * Sync contract (monolith syncLegacy, seg3 IIFE):
 *   - builder persona → UTM Targeting, unconditionally (builder wins, silently)
 *   - builder audsCode → Email Audience Type when the picklist has that code
 *   - an audType edit flows BACK into the builder via FormView's listener
 * autoSF contract (monolith L4684–4688): sfInclude tracks the recommended
 * primary-tier reports until the user's own edit differs from the last
 * suggestion. (The ✨ hint row + Re-suggest button were removed per Jack
 * 2026-08-21 — intentional divergence from the monolith; autoSF still fills.)
 */
import { useEffect, useRef } from "react";
import "./send-scope.css";
import type { FormState } from "../services/submit";
import type { SegState } from "../services/segData";
import { audsCode } from "../services/segData";
import {
  SENDER_TYPE, UTM_TARGET,
  AUD_TYPE, isAudTypeCode, SCOPE_TIP,
} from "../services/sendScopeData";
import Combo from "./Combo";
import { useLiveOptions } from "../services/useFieldOptions";

interface Props {
  form: FormState;
  setForm: (f: FormState | ((prev: FormState) => FormState)) => void;
  seg: SegState;
  /** Rendered directly under the "Audience" heading, above Sender Type. FormView
   *  passes Primary Message in here — moved off step 2 per Katy Stenerson
   *  2026-08-19 ("move the Primary message to be in the Audience page"). It stays
   *  OWNED by FormView (msgOpts, the F writer, and fmiss all live there); this is
   *  a placement slot, not a second copy of the field. */
  topSlot?: React.ReactNode;
}

/**
 * Sender Type from Step 1's focus — EXACT matches only, "" when it cannot be known.
 *
 * Returning "" is the point: a blank control is a question the requester will answer,
 * where a wrong pre-filled one is a question they never see.
 */
export function senderTypeFromFocus(f: { reqFocus?: string; masterplan?: string; community?: string }): string {
  /* the FormState key is `reqFocus` — Initiative Request Focus. Keyed on `focus` first
     and the derivation silently returned "" for everything, which is exactly the class
     of bug this helper exists to prevent: a wrong key reads as "cannot be inferred". */
  const focus = String(f.reqFocus || "").trim().toLowerCase();
  if (focus === "market") return "Market";
  if (focus.indexOf("communit") > -1 || focus.indexOf("masterplan") > -1) {
    /* a masterplan pick is a masterplan send; otherwise it is a community send */
    if (String(f.masterplan || "").trim()) return "Masterplan";
    if (String(f.community || "").trim()) return "Community";
    return "";                       /* focus chosen, nothing picked yet */
  }
  return "";                         /* Division / Area of Interest have no counterpart */
}

const Info = ({ tip }: { tip: string }) => <span className="info" data-tip={tip}>i</span>;

export default function SendScope({ form, setForm, seg, topSlot }: Props) {
  /* functional updates — the stale-closure spreads lost sibling writes (the
     2026-08-09 straight-through-submit race: the mount seed's senderType write
     was dropped by a same-commit sibling setForm) */
  const F = (patch: Partial<FormState>) => setForm((prev) => ({ ...prev, ...patch }));
  const formRef = useRef(form);
  formRef.current = form;
  const Fr = F;

  /* seed the monolith defaults on first mount (radioList dflt + AUD_TYPE LO).
     Via Fr (fresh ref), NOT F: the stale-closure spread could clobber writes
     that landed between App renders — caught 2026-08-09 when a scripted
     straight-through submit bounced to step 3 with senderType empty even
     though the combo displayed "Market" (lost-update race; the monolith's
     checked radios can't race). */
  /**
   * AUDIENCE STARTS BLANK. ONLY WHAT STEP 1 ACTUALLY DETERMINES IS FILLED IN.
   * (Jack, 2026-08-22: "update the audience form to default to blank … but can auto
   * populate info based on what we know from page 1 … however DIVCode Market City
   * Code Leads 3Y / DIVCode Customer Profile — FTHB Leads 3Y should be blank along
   * with others as we'd never know / cannot be inferred".)
   *
   * This used to stamp three hardcoded defaults on mount — Market / FTHB / LO —
   * which is a GUESS wearing the clothes of an answer: the requester sees a filled
   * form, has no reason to look at it, and a wrong Buyer Demo or Audience Type ships
   * to a real list. Nothing about Step 1 says the send is to first-time buyers, or to
   * Leads & Opportunities rather than Homeowners.
   *
   * SENDER TYPE IS THE ONE EXCEPTION, and only where the mapping is exact. Step 1's
   * Initiative Request Focus already answers "what does the from represent":
   *   Market                    → "Market"
   *   Communities / Masterplans → "Masterplan" for a masterplan pick, else "Community"
   * Division and Area of Interest have NO Sender Type counterpart in the list
   * (Market / MarketSpanish / Masterplan / Community / City), and MarketSpanish and
   * City cannot be told from the focus at all — so those stay blank rather than
   * defaulting to Market and being wrong quietly.
   */
  useEffect(() => {
    const derived = senderTypeFromFocus(formRef.current);
    if (!derived) return;
    setForm((prev) => (prev.senderType ? prev : { ...prev, senderType: derived }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* monolith syncLegacy: the builder writes these two fields on every builder
     change — silently, no touched-guard: the builder wins. */
  /**
   * THE BUILDER STILL WINS — BUT ONLY ONCE IT HAS ACTUALLY BEEN USED (2026-08-22).
   *
   * These two effects mirror the segmentation builder into Buyer Demo and Email
   * Audience Type, and the builder must keep overriding both. What they must NOT do is
   * fire on the builder's own INITIAL state: `segData`'s SEG_INIT ships
   * `persona: "FTHB"` and an auds set that codes to "LO", so mounting Step 3 wrote both
   * fields before the requester had looked at either. That is where Jack's pre-filled
   * "FTHB" and "LO = Leads & Opportunities" came from.
   *
   * Skipping the FIRST run is the exact distinction: a real builder change re-fires the
   * effect and syncs as before, while the defaults-on-mount pass writes nothing.
   */
  const personaFirst = useRef(true);
  useEffect(() => {
    if (personaFirst.current) { personaFirst.current = false; return; }
    if (seg.persona && seg.persona !== formRef.current.utmTarget) Fr({ utmTarget: seg.persona });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seg.persona]);
  const audsFirst = useRef(true);
  useEffect(() => {
    if (audsFirst.current) { audsFirst.current = false; return; }
    const code = audsCode(seg.auds);
    if (isAudTypeCode(code) && code !== formRef.current.emailAudienceType) Fr({ emailAudienceType: code });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seg.auds.L, seg.auds.O, seg.auds.R, seg.auds.H]);

  /* ── autoSF (monolith L4684) — sfInclude tracks the recommendation until the
     user's own edit differs from the last suggestion ── */
  /**
   * THE SALESFORCE LISTS ARE NEVER GUESSED (2026-08-22).
   *
   * `autoSF` used to write `resolveAudience(...)`'s primary tier into the Include box
   * on every Step-1 change. The names it produced read like real reports but are
   * TEMPLATES — Jack's own example, "DIVCode Market City Code Leads 3Y" and "DIVCode
   * Customer Profile — FTHB Leads 3Y", carries the literal token `DIVCode` and a
   * persona nobody chose. A requester who trusts that ships a send pointed at a
   * report name that does not exist, and the failure surfaces in SFMC rather than
   * here.
   *
   * A report name is knowable only by someone who looked it up in Salesforce, so this
   * box starts and stays EMPTY unless a human types it or a duplicated job brings one
   * over. `resolveAudience` is untouched and still available for guidance elsewhere —
   * it just no longer writes into the field.
   */
  const sfTouched = useRef(false);

  /* LIVE PICKLISTS (2026-08-18). Sender Type and Email Audience Type now come from the
     production Projects schema via the worker, so an option the ops team adds in Airtable
     appears here without a release. The constants below stay as the fallback — they render
     until the fetch resolves and remain in use if it fails, so there is never an empty
     control. See services/useFieldOptions.ts.

     UTM_TARGET has no Airtable counterpart (it's a UTM-convention list, not a Projects
     field), so it stays hardcoded — pointing it at a field name that doesn't exist would
     just silently fall back forever and imply a link that isn't there. */
  const liveSender = useLiveOptions("Sender Type", SENDER_TYPE);
  const liveAud = useLiveOptions("Email Audience Type (for UTM 2026)", []);

  const senderOpts = liveSender.map((s) => ({ v: s }));
  const utmOpts = UTM_TARGET.map((u) => ({ v: u.v }));
  /* Airtable stores these as one string, "LO = Leads & Opportunities", but this control's
     VALUE has to stay the bare code — the UTM builder and `isAudTypeCode` both depend on it.
     So split on the separator and keep the full string as the label. Anything that doesn't
     match that shape is skipped rather than guessed at, and an empty result falls back to the
     built-in AUD_TYPE pairs. */
  const liveAudPairs = liveAud
    .map((s) => { const i = s.indexOf(" = "); return i > 0 ? ([s.slice(0, i).trim(), s] as [string, string]) : null; })
    .filter((x): x is [string, string] => !!x);
  const audOpts = (liveAudPairs.length ? liveAudPairs : AUD_TYPE).map(([v, l]) => ({ v, label: l }));

  return (
    <div className="sendscope">
      {/* monolith .mvp-aud-hd (visible in MVP mode only — full mode mounts this
          whole panel hidden) */}
      <div className="mvp-aud-hd">
        <h3 className="sechd"><span className="b">✦</span><span className="t">Audience</span><span className="rule" /></h3>
        <p style={{ margin: "-4px 0 16px", fontSize: 13, color: "#6a7381", lineHeight: 1.5 }}>
          Pick the audience type, then paste the Salesforce report/list names to send to — one per line. Add lists to exclude if needed.
        </p>
      </div>

      {topSlot}

      <div className="grid2">
        <div className="f">
          <label>Sender Type <span className="req">*</span> <Info tip={SCOPE_TIP.senderType} /></label>
          <Combo value={form.senderType || ""} onChange={(v) => F({ senderType: v })} options={senderOpts} />
        </div>
        <div className="f">
          {/* LABEL ONLY (Jack 2026-08-19). The state key, the payload field, the
              `utmTarget` constant list and everything the backend reads are all
              unchanged — the monolith still shows "UTM Targeting" and would need
              the same relabel to match. */}
          <label>Buyer Demo for Targeting <span className="req">*</span> <Info tip={SCOPE_TIP.utmTarget} /></label>
          <Combo value={form.utmTarget || ""} onChange={(v) => F({ utmTarget: v })} options={utmOpts} placeholder="— select —" />
        </div>
      </div>

      <div className="f">
        <label>Email Audience Type <span className="req">*</span> <Info tip={SCOPE_TIP.audType} /></label>
        <Combo value={form.emailAudienceType || ""} onChange={(v) => F({ emailAudienceType: v })} options={audOpts} placeholder="— select —" />
      </div>

      <div className="grid2">
        <div className="f">
          <label>Salesforce List(s) to Include <span className="req">*</span> <Info tip={SCOPE_TIP.sfInclude} /></label>
          <textarea value={form.sfListsInclude || ""}
            placeholder={"One Salesforce report/list name (or SF report link) per line — e.g.\nAUS_AllLeads_AllTime\nAUS_ContactswithOpps"}
            onChange={(e) => { sfTouched.current = e.target.value.trim() !== ""; F({ sfListsInclude: e.target.value }); }} />
          {/* NOTHING AUTO-FILLS HERE (2026-08-22). The ✨ hint row went in 2026-08-21;
              the auto-suggest itself went now, because the names it wrote were
              templates containing the literal token "DIVCode" and a persona nobody
              chose. `sfTouched` is kept as the "the requester typed something" signal
              a duplicate-job prefill must not overwrite. */}
        </div>
        <div className="f">
          <label>Salesforce List(s) to Exclude <Info tip={SCOPE_TIP.sfExclude} /></label>
          <textarea value={form.sfListsExclude || ""}
            placeholder="One Salesforce report/list name per line (optional)"
            onChange={(e) => F({ sfListsExclude: e.target.value })} />
        </div>
      </div>

      <div className="f">
        <label>Sender Profile <Info tip={SCOPE_TIP.senderProfile} /></label>
        <input value={form.senderProfile || ""} placeholder="Lennar [Market]"
          onChange={(e) => F({ senderProfile: e.target.value })} />
      </div>

      {/* "Is this an approved Pilot or A/B Test?" REMOVED 2026-08-19 per Jack. Deliberate
          divergence from the monolith, which still asks it. The state key
          (`approvedExperiment`) and everything downstream of it are left intact so the
          payload shape doesn't change — the question simply isn't asked any more. */}
    </div>
  );
}
